import React, { useCallback } from 'react';
import FrameworkUtils from 'utils/framework';
import Chiclet from 'components/Chiclet';
import Badge from 'components/Badge';
import { Flex, Text } from 'components/ui';
import { colors, space } from 'style/config';
import { trackWelcomeMatCategoryView } from 'ai/analytics';

const { wrapFunctionalComponent } = FrameworkUtils;

const PromptButton = React.memo(({
    text, badge, slotNumber, onPromptClick, isDisabled
}) => {
    const handleClick = useCallback(
        e => {
            e.stopPropagation();
            onPromptClick(text, slotNumber);
        },
        [onPromptClick, text, slotNumber]
    );

    return (
        <Chiclet
            variant='shadow'
            css={{
                height: 40,
                opacity: isDisabled ? 0.5 : 1,
                cursor: isDisabled ? 'not-allowed' : 'pointer'
            }}
            {...(!isDisabled && { onClick: handleClick })}
        >
            <Flex
                width='100%'
                alignItems='center'
                justifyContent='space-between'
            >
                <span>{text}</span>
                {badge && (
                    <Flex marginLeft={2}>
                        <Badge
                            badge={badge}
                            color={colors.black}
                        />
                    </Flex>
                )}
            </Flex>
        </Chiclet>
    );
});

PromptButton.displayName = 'PromptButton';

function PromptCategory(props) {
    const { title, prompts, onPromptClick, isDisabled = false } = props;

    const handleCategoryClick = useCallback(() => {
        trackWelcomeMatCategoryView({ categoryName: title });
    }, [title]);

    const handlePromptClick = useCallback(
        (promptText, slotNumber) => {
            onPromptClick(promptText, { cardName: title, slotNumber });
        },
        [onPromptClick, title]
    );

    return (
        <Flex
            css={styles.categoryContainer}
            flexDirection='column'
            gap={3}
            padding={4}
        >
            <Text
                onClick={handleCategoryClick}
                fontSize='base'
                fontWeight='bold'
                css={styles.categoryTitle}
            >
                {title}
            </Text>
            <Flex
                gap={2}
                flexDirection='column'
            >
                {prompts.map((prompt, index) => (
                    <PromptButton
                        key={prompt.id}
                        text={prompt.text}
                        badge={prompt.badge}
                        slotNumber={index + 1}
                        onPromptClick={handlePromptClick}
                        isDisabled={isDisabled}
                    />
                ))}
            </Flex>
        </Flex>
    );
}

const styles = {
    categoryContainer: {
        backgroundColor: 'rgba(255, 255, 255, 0.3)',
        borderRadius: space[2],
        minWidth: 242
    },
    categoryTitle: {
        cursor: 'pointer'
    }
};

export default wrapFunctionalComponent(PromptCategory, 'PromptCategory');
